import type { ResolvedFeatures } from './role-registry';
import type { FrbacUser } from '../types';
import { BUILT_IN_ROLES } from './built-in-roles';
import { FEATURE_GROUP_REGISTRY, type FeatureGroupRegistry } from '../registry';

function parseFeatureToArray(featureGuids: string | string[]) {
  const parsedFeatures: string[] = Array.isArray(featureGuids) ? featureGuids : [featureGuids];
  return parsedFeatures;
}

const nonPermissionRoleLabels = new Set(['authenticated', 'waiting-approval']);

export function mapRoleWithGuid(user: FrbacUser, orgId: string) {
  const roles = user.roles ?? {};
  const rolesOrg = roles[orgId];
  if (!rolesOrg) {
    return [];
  }
  return [
    ...new Set(
      roles[orgId]
        .map((role) => {
          if (role === 'admin') return BUILT_IN_ROLES.ADMIN.guid;
          if (role === 'application') return BUILT_IN_ROLES.USER.guid;
          return role;
        })
        .filter((role) => !nonPermissionRoleLabels.has(role))
    ),
  ];
}

/**
 * Check if a set of features has access based on permissions
 * @param allowedFeaturesIds The permissions needed to have access
 * @param featuresSet The set of permissions to check
 */
export function hasAccess(allowedFeaturesIds: string[] | string, featuresSet: ResolvedFeatures): boolean {
  allowedFeaturesIds = parseFeatureToArray(allowedFeaturesIds);
  return allowedFeaturesIds.some((guid) => {
    // Layer 2: shadow override is final authority
    if (featuresSet.overrides.deny.has(guid)) return false;
    if (featuresSet.overrides.allow.has(guid)) return true;

    // Layer 1: regular role combination (deny wins)
    if (featuresSet.deny.size > 0 && featuresSet.deny.has(guid)) return false;
    return featuresSet.allow.has(guid);
  });
}

/**
 * Returns true if the user has access to any descendant leaf feature
 * within the given feature group. Uses FEATURE_GROUP_REGISTRY by default;
 * accepts an optional registry override for testing.
 */
export function hasGroupAccess(
  groupGuid: string,
  features: ResolvedFeatures,
  registry: FeatureGroupRegistry = FEATURE_GROUP_REGISTRY
): boolean {
  const descendants = registry.get(groupGuid);
  if (!descendants || descendants.size === 0) return false;
  return hasAccess([...descendants], features);
}
